<template>
  <SectionTemplate title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" @contact="handleContact" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { showDynamicContact } from '../../utils/contact';

const linksConfig = {
  'ai-voice-generation':
    'https://www.youyan3d.com/blog/Supernatural-Multistyle-Text-to-Language-for-Professional-Scenarios.html',
  'multi-language-tts':
    'https://www.youyan3d.com/blog/Master-Multilingual-Videos-from-Translation-to-Voice.html',
  'voice-workspace': 'https://www.youyan3d.com/workspace/voice',
  'corporate-training': 'https://www.youyan3d.com/scenarios/corporate-training.html',
  pedagogical: 'https://www.youyan3d.com/scenarios/pedagogical.html',
  'radio-media': 'https://www.youyan3d.com/scenarios/radio-and-television-media.html',
  'cloning-blog': 'https://www.youyan3d.com/blog/Avatar-Cloning-and-Voice-Cloning.html',
};

// 将链接配置转换为 FaqLink 格式
const links = Object.entries(linksConfig).map(([key, url]) => ({ key, url }));

// 使用 FAQ 辅助工具
const { getLink, getStrong, getContactLink } = useFaqHelper(links);

// FAQ 数据列表
const faqList = [
  {
    question: '什么是AI语音生成（TTS）？',
    answer: `魔珐有言的AI语音生成，又称“${getLink('ai-voice-generation', '文本转语音（TTS）')}”，是一种将书面文字自动转换为逼真人类“语音”的技术。我们的引擎在此基础上，融入了情感表达和多语言支持。`,
    isOpen: false,
  },
  {
    question: '魔珐有言的AI语音支持哪些语言和方言？',
    answer: `我们支持包括中文、英语、日语、韩语在内的${getLink('multi-language-tts', '超过20种语言')}，同时提供粤语、四川话等多种方言音色，${getLink('voice-workspace', '拥有超过400种风格的声音')}，满足您的本地化和个性化需求。`,
    isOpen: false,
  },
  {
    question: 'AI语音的语速、语调可以调整吗？',
    answer: `可以。我们提供强大的声音编辑功能，您可以在生成语音后，自由调整语速、停顿、重音和语调，对任意片段进行精细打磨，直到满意为止。`,
    isOpen: false,
  },
  {
    question: 'AI语音生成的主要应用场景有哪些？',
    answer: `广泛应用于${getLink('corporate-training', '企业培训')}、${getLink('pedagogical', '在线教育')}、${getLink('radio-media', '媒体播报')}、有声书制作、电商带货等多种需要高效、高质量音频内容的场景。`,
    isOpen: false,
  },
  {
    question: '我的视频课程想换个生动的旁白，AI语音能胜任吗？',
    answer: `非常适合。您可以${getLink('voice-workspace', '从海量音色库中')}选择最匹配课程风格的高仿真音色，并为其赋予亲切、专业或富有激情的情感，让课程讲解更加生动，有效提升学员的注意力和学习体验。`,
    isOpen: false,
  },
  {
    question: 'AI语音适合用于企业内部的标准化培训视频吗？',
    answer: `非常适合。${getLink('corporate-training', '使用AI语音制作培训材料，可以确保信息传递的标准化与统一性')}，避免因不同讲师状态差异导致的理解偏差，同时支持内容随需快速迭代更新，提升培训效率。`,
    isOpen: false,
  },
  {
    question: '在电商带货视频中，如何让AI配音更具说服力？',
    answer: `关键在于${getStrong('“情感的表达”')}。您可以选择充满${getStrong('“热情与感染力”')}的音色，并利用${getStrong('“声音编辑”')}功能，在关键产品卖点处设置重音与停顿，从而有效引导顾客情绪，提升转化率。`,
    isOpen: false,
  },
  {
    question: 'AI语音生成有声书或儿童故事的效果怎么样？',
    answer: `效果出众。您可以为不同的故事角色分配不同音色，并赋予温柔、神秘或欢快等相应情绪，打造沉浸式的${getStrong('“角色扮演”')}听觉体验，让故事讲述栩栩如生。`,
    isOpen: false,
  },
  {
    question: '产品的收费模式是怎样的？是按生成时长计费吗？',
    answer: `我们提供灵活透明的计费方式，主要依据${getStrong('“语音生成时长”')}进行计费。在使用声音克隆和Pro级音色时，将根据实际生成的配音时长进行结算。`,
    isOpen: false,
  },
  {
    question: '“声音克隆”具体是如何实现的？需要我提供什么？',
    answer: `${getLink('cloning-blog', '您仅需提供一段时长约10秒、音质清晰的发言人原始音频')}，我们的AI模型即可进行深度学习，短短几分钟内即可完成声音模型的训练，并高保真复刻其独特的音色、语调和发音习惯，生成专属的语音资产。`,
    isOpen: false,
  },
  {
    question: '如果现有的音色都不满足需求，可以定制专属音色吗？',
    answer: `可以。我们有言官方提供专业的音色定制服务，可根据您的品牌调性或特定需求，为您量身打造独一无二的优质音色。您可以${getContactLink('contact', '联系我们')}获取详细方案。`,
    isOpen: false,
  },
];

// 处理联系方式点击
const handleContact = (action: string) => {
  if (action === 'contact') {
    showDynamicContact(1078922, '企业级数字人定制', '1V1专属咨询服务');
  } else if (action === 'api-sdk') {
    showDynamicContact(1431230, 'API/SDK接入咨询', '1对1技术演示支持');
  }
};
</script>
